import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Edit3, Search, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductLibrary() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formName, setFormName] = useState("");
  const [formCategory, setFormCategory] = useState("beef");

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`, { params: search ? { search } : {} });
      setProducts(res.data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openAdd = () => {
    setEditingProduct(null);
    setFormName("");
    setFormCategory("beef");
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setFormName(product.name);
    setFormCategory(product.category);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Enter a product name");
      return;
    }
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, {
          name: formName,
          category: formCategory,
        });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, {
          name: formName,
          category: formCategory,
        });
        toast.success("Product added");
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    }
  };

  const handleDelete = async (product) => {
    try {
      await axios.delete(`${API}/products/${product.id}`);
      toast.success(`"${product.name}" deleted`);
      fetchProducts();
    } catch {
      toast.error("Failed to delete product");
    }
  };

  const categories = ["beef", "pork", "poultry", "lamb"];

  return (
    <div data-testid="product-library-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
        <div>
          <h1
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            PRODUCT LIBRARY
          </h1>
          <p className="text-sm text-muted-foreground tracking-wider uppercase mt-1">
            MANAGE YOUR MEAT CUT CATALOG
          </p>
        </div>
        <Button
          data-testid="add-product-btn"
          onClick={openAdd}
          className="h-14 px-6 text-base font-bold uppercase tracking-wider rounded-sm glow-orange bg-primary text-primary-foreground hover:brightness-110 active:scale-95 transition-all duration-75 w-full sm:w-auto"
        >
          <Plus className="w-5 h-5 mr-2" />
          ADD PRODUCT
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          data-testid="product-search-bar"
          placeholder="SEARCH PRODUCTS..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
          className="h-14 pl-12 bg-input border-2 border-border focus:border-primary text-lg font-mono rounded-sm uppercase"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border border-border/50 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border/50 hover:bg-transparent">
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4 px-5">
                PRODUCT NAME
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4">
                CATEGORY
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4 text-right pr-5">
                ACTIONS
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-12 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-12">
                  <Package className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground text-sm">
                    {search ? "No products match your search" : "No products yet"}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow
                  key={product.id}
                  data-testid={`product-row-${product.id}`}
                  className="border-border/30 hover:bg-secondary/30 transition-colors"
                >
                  <TableCell className="py-4 px-5">
                    <span className="font-bold text-sm uppercase tracking-wide">
                      {product.name}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="text-xs font-mono tracking-wider uppercase px-2 py-1 bg-secondary rounded-sm text-muted-foreground">
                      {product.category}
                    </span>
                  </TableCell>
                  <TableCell className="py-4 pr-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        data-testid={`edit-product-${product.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(product)}
                        className="h-10 w-10 hover:text-primary"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-product-${product.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(product)}
                        className="h-10 w-10 hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle
              className="uppercase tracking-wider"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {editingProduct ? "EDIT PRODUCT" : "ADD PRODUCT"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                PRODUCT NAME
              </label>
              <Input
                data-testid="product-name-input"
                placeholder="E.g., Bone-In Ribeye"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                autoComplete="off"
                className="h-12 bg-input border-2 border-border focus:border-primary font-mono rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                CATEGORY
              </label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger
                  data-testid="product-category-select"
                  className="h-12 bg-input border-2 border-border font-mono rounded-sm uppercase"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat} className="uppercase">
                      {cat.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="save-product-btn"
              onClick={handleSave}
              className="bg-primary text-primary-foreground rounded-sm uppercase tracking-wider font-bold"
            >
              {editingProduct ? "UPDATE" : "ADD PRODUCT"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
